import StoreIcon from "@mui/icons-material/Store";
import ImportantDevicesIcon from "@mui/icons-material/ImportantDevices";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SettingsSystemDaydreamOutlinedIcon from "@mui/icons-material/SettingsSystemDaydreamOutlined";
import AreaChartIcon from "@mui/icons-material/AreaChart";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBoxOpen, faCommentDollar } from "@fortawesome/free-solid-svg-icons";
import { useClaims } from "../../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../../auth/authorizationTypes";
import useGetCompanyStandards from "@features/ecommerce/hooks/useGetCompanyStandards";
import FolderIcon from "@mui/icons-material/Folder";
import { useFeatureFlag } from "shared-ui/src/utils/FeatureFlagHelper";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import {
  MERGE_LORDS_ENABLE_PRODUCT_LISTING_PAGE_FEATURE_FLAG,
  PRODUCT_LISTING_BASE_PATH,
} from "../../../pages/ecommerce/product-listing/productListingRoutes";

type StandardsGroup = {
  groupID?: number | null;
  linkText?: string | null;
  displayOrder?: number | null;
  children?: StandardsGroup[] | null;
};

export function CleanStandardsFolderName(name: string) {
  return name.replace(/["!@#$%^&*()+{}[\]:;'<>./?|\\ ]/g, "").toLowerCase();
}

export default function useGetStorefrontNavItems() {
  const { hasPermission, getClaimValue } = useClaims();

  const shiComBaseUrl = import.meta.env.SHICOM_BASE_URL;

  const orgUnitId = Number(getClaimValue("orgUnitIdCurrent"));
  const { data: companyStandardsData, isLoading: isCompanyStandardsLoading } =
    useGetCompanyStandards(orgUnitId.toString());
  const { enabled: standardsShopEnabled } = useFeatureFlag(
    "nomads.enable-standards-shop",
    false,
  );
  const { enabled: cspSelfServiceEnabled } = useFeatureFlag(
    "cartographers.cloud-subscriptions-mvp",
    false,
  );
  const { enabled: invoicePortalEnabled } = useFeatureFlag(
    "synergists.enable-invoice-portal",
    false,
  );

  const {
    enabled: productListingEnabled,
    loading: productListingFlagLoading,
  } = useFeatureFlag(
    MERGE_LORDS_ENABLE_PRODUCT_LISTING_PAGE_FEATURE_FLAG,
    false,
  );

  const standardsPath = standardsShopEnabled
    ? "/standards-shop"
    : `${shiComBaseUrl}/shop/standards`;

  const getCompanyStandards = () => {
    if (isCompanyStandardsLoading) {
      return [
        {
          id: "standards-folder-loading",
          type: "Link",
          displayName: "—",
          visible: false,
          icon: <FolderIcon />,
          path: `${shiComBaseUrl}/standards`,
          level: 0,
        },
      ];
    }
    const groups = companyStandardsData?.standardsGroups ?? [];

    const firstLevel = groups[0]?.children ?? [];

    return firstLevel
      .filter((g) => g?.groupID)
      .sort((a, b) => {
        const orderA = a.displayOrder ?? Number.MAX_SAFE_INTEGER;
        const orderB = b.displayOrder ?? Number.MAX_SAFE_INTEGER;
        if (orderA !== orderB) return orderA - orderB;

        const textA = (a.linkText ?? "").trim().toLowerCase();
        const textB = (b.linkText ?? "").trim().toLowerCase();
        return textA.localeCompare(textB);
      })
      .map((child) => {
        const groupId = child.groupID!;
        const pathName = CleanStandardsFolderName(child.linkText ?? "");

        return {
          id: `standards-folder-${groupId}`,
          type: "Link",
          displayName: child.linkText?.trim() || "Untitled Folder",
          visible: true,
          icon: <FolderIcon />,
          path: standardsShopEnabled
            ? `${standardsPath}?folder=${encodeURIComponent(pathName)}`
            : `${shiComBaseUrl}/shop/standards/${pathName}`,
          level: 1, // indent one level
          children: [],
          sx: { py: 0.1 },
        } as StandardsGroup;
      });
  };

  if (!orgUnitId) return false;

  return [
    {
      id: "shopProducts",
      type: "Link",
      displayName: "Shop Products",
      // Hidden until the PLP flag settles so the link never points at the wrong destination.
      visible:
        hasPermission(ShiOnePermission.productView) && !productListingFlagLoading,
      icon: <StoreIcon />,
      path: productListingEnabled
        ? PRODUCT_LISTING_BASE_PATH
        : `${shiComBaseUrl}/shop/search`,
    },
    {
      id: "standards",
      type: "Dashboard",
      displayName: "Standards",
      visible: hasPermission(ShiOnePermission.featuredProductsView),
      icon: <ImportantDevicesIcon />,
      path: standardsPath,
      children: getCompanyStandards(),
    },
    {
      id: "orders",
      type: "Link",
      displayName: "Orders",
      visible:
        hasPermission(ShiOnePermission.orderView) ||
        hasPermission(ShiOnePermission.orderSearchMy) ||
        hasPermission(ShiOnePermission.orderSearchOrgUnit),
      icon: <FontAwesomeIcon icon={faBoxOpen} />,
      path: "/orders/management",
    },
    {
      id: "quotes",
      type: "Link",
      displayName: "Quotes",
      visible:
        hasPermission(ShiOnePermission.quoteViewMy) ||
        hasPermission(ShiOnePermission.quoteViewOrgUnit),
      icon: <FontAwesomeIcon icon={faCommentDollar} />,
      path: "/quotes",
    },
    {
      id: "invoices",
      type: "Link",
      displayName: "Invoices",
      visible:
        invoicePortalEnabled &&
        (hasPermission(ShiOnePermission.orderView) ||
          hasPermission(ShiOnePermission.orderSearchMy) ||
          hasPermission(ShiOnePermission.orderSearchOrgUnit)),
      icon: <ReceiptLongIcon />,
      path: "/invoices/management",
    },
    {
      id: "subscriptionManagement",
      type: "Link",
      displayName: "Subscription Management",
      visible: hasPermission(ShiOnePermission.cloudSubsRead),
      icon: <SettingsSystemDaydreamOutlinedIcon />,
      // Keep legacy SHI.com access until CSP self-service is flagged on for the org.
      path: cspSelfServiceEnabled
        ? "/cloud/subscription-management"
        : `${shiComBaseUrl}/CloudSubscriptions`,
    },
    {
      id: "renewalTimeline",
      type: "Link",
      displayName: "Renewal Timeline",
      visible: hasPermission(ShiOnePermission.renewalsView),
      icon: <CalendarMonthIcon />,
      path: `${shiComBaseUrl}/RenewalTimeLine/RenewalTimeLine.aspx`,
    },
    {
      id: "reporting",
      type: "Link",
      displayName: "Reporting",
      visible: hasPermission(ShiOnePermission.reportingRead),
      icon: <AreaChartIcon />,
      path: `${shiComBaseUrl}/Reports/Home`,
    },
    {
      id: "divider-section-1",
      type: "Divider",
      displayName: "",
      visible: true,
    },
  ];
}
